import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HvTabs, HvTab } from '@hitachivantara/uikit-react-core';
import { Icon, Label, colors } from '@iq/react-components';

import { getTimezone } from '../../../../bundles/application';
import {
  getSourceEventsTotal,
  getAlarmsLogbookTotal,
  getAlarmById,
  fetchAlarmSourceEvents,
  fetchAlarmLogbook,
  deleteAlarm,
} from '../../../../bundles/alarms';
import { getCommentsCount, fetchComments } from '../../../../bundles/comments';
import { getHasPermission } from '../../../../bundles/auth';
import { getActiveSite } from '../../../../bundles/sites';
import { formatSiteDatetime } from '../../../../datetimeUtils';
import config from '../../../../config';

import SimpleModal from '../../../SimpleModal/SimpleModal';
import ConfirmationDialog from '../../../ConfirmationDialog';
import { DropdownMenu } from '../../../DropdownMenu';
import {
  EventTimelineProvider,
  useActiveListEvents,
} from '../../EventTimelinePanel/EventTimelineState';

import { SEVERITY_COLORS } from '../constants';

import AlarmHandlerTab from './AlarmModalTabs/AlarmHandlerTab';
import SourceEventsTab from './AlarmModalTabs/SourceEventsTab';
import CommentsTab from './AlarmModalTabs/CommentsTab';
import LogbookTab from './AlarmModalTabs/LogbookTab';
import UserEventsTab from './AlarmModalTabs/UserEventsTab';
import './AlarmModal.scss';

const MODAL_TABS = {
  HANDLER: 0,
  SOURCE_EVENTS: 1,
  USER_EVENTS: 2,
  LOGBOOK: 3,
  COMMENTS: 4,
};

// Reports the currently-linked user-events count up from inside the EventTimelineProvider
// (useActiveListEvents relies on context, so it can't be called directly in AlarmModal).
const UserEventsCount = ({ onCountChange }) => {
  const { totalEventCount } = useActiveListEvents(undefined, false);

  useEffect(() => {
    onCountChange(totalEventCount);
  }, [totalEventCount, onCountChange]);

  return null;
};

const AlarmModal = ({ alarm: alarmProperty, onClose, alarmQueryParameters }) => {
  const [activeModalTab, setActiveModalTab] = useState(MODAL_TABS.HANDLER);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userEventsCount, setUserEventsCount] = useState(-1);
  const dispatch = useDispatch();
  const timezone = useSelector(getTimezone);
  const activeSite = useSelector(getActiveSite);

  const liveAlarm = useSelector((state) => getAlarmById(state, alarmProperty?.id));
  const alarm = liveAlarm || alarmProperty;

  // Get counts for tabs
  const sourceEventsCount = useSelector((state) => getSourceEventsTotal(state, alarm?.id));
  const logbookCount = useSelector((state) => getAlarmsLogbookTotal(state, alarm?.id));
  const commentsCount = useSelector((state) => getCommentsCount(state, alarm?.id));
  const canDeleteAlarms = useSelector((state) =>
    getHasPermission(
      state,
      'alarms/Delete',
      config.onPremise ? undefined : { org: alarm?.org, site: alarm?.site }
    )
  );

  const relatedEventsValue = useMemo(() => {
    const value = alarm?.relatedEvents || {};
    return value.eventTypes ? value : { ...value, eventTypes: 'user' };
  }, [alarm?.relatedEvents]);

  // Nothing has been linked yet, so there's no saved range/filters to query with.
  const hasLinkedEvents = Boolean(relatedEventsValue.from && relatedEventsValue.to);

  const relatedEventsDateDomain = useMemo(() => {
    const from = relatedEventsValue.from && new Date(relatedEventsValue.from).getTime();
    const to = relatedEventsValue.to && new Date(relatedEventsValue.to).getTime();
    if (from && to) return [from, to];
    // An empty domain would make useActiveListEvents fall back to `Date.now()` on every
    // render (unstable dependency -> infinite fetch loop), so use a fixed ~6mo fallback instead.
    const now = Date.now();
    return [now - 6 * 30 * 24 * 60 * 60 * 1000, now];
  }, [relatedEventsValue.from, relatedEventsValue.to]);

  const eventTimelineKey = useMemo(
    () =>
      JSON.stringify([
        relatedEventsValue.from,
        relatedEventsValue.to,
        relatedEventsValue.text,
        relatedEventsValue.active,
        relatedEventsValue.params,
      ]),
    [relatedEventsValue]
  );

  useEffect(() => {
    if (!alarm?.id) return;

    dispatch(fetchAlarmSourceEvents(alarm));
    dispatch(fetchAlarmLogbook(alarm));
    dispatch(fetchComments({ parentId: alarm.id }));
  }, [alarm, dispatch]);

  const isResolved = alarm?.state === 'resolved';

  const handleAlarmUpdate = useCallback(() => {
    onClose();
  }, [onClose]);

  const onConfirmDeletion = useCallback(() => {
    setShowDeleteConfirm(false);
    dispatch(deleteAlarm(alarm?.id));
    onClose();
  }, [alarm, dispatch, onClose]);

  const editMenuOptions = useMemo(() => {
    const options = [];
    if (isResolved && canDeleteAlarms) {
      options.push({
        component: <div>Delete alarm</div>,
        onSelect: () => setShowDeleteConfirm(true),
      });
    }
    return options;
  }, [isResolved, canDeleteAlarms]);

  const deleteAlarmConfirmation = useMemo(
    () =>
      showDeleteConfirm ? (
        <ConfirmationDialog
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={onConfirmDeletion}
          confirmType="danger"
          confirmText="Delete"
          showCancel
          title="Delete Alarm"
          body={
            <>
              <p>
                Are you sure you want to PERMANENTLY delete this resolved alarm? Resolved alarm
                details can provide valuable insights for handling similar alarms in the
                future.{' '}
              </p>
              <p>Deleting this will also remove it from the logbook.</p>
            </>
          }
        />
      ) : null,
    [showDeleteConfirm, onConfirmDeletion]
  );

  const renderSeverity = (severity) => {
    const color = SEVERITY_COLORS[severity?.toLowerCase()] || '#666';
    return (
      <div className="alarm-modal-severity">
        <Icon
          icon="he-statusrisk"
          size="s"
          color={color}
        />
        <span style={{ textTransform: 'capitalize' }}>{severity || '-'}</span>
      </div>
    );
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    return formatSiteDatetime(dateValue, timezone);
  };

  const counts = {
    [MODAL_TABS.SOURCE_EVENTS]: sourceEventsCount > 0 ? ` (${sourceEventsCount})` : '',
    // Gate on hasLinkedEvents too: userEventsCount is stale once unlinked, since
    // UserEventsCount unmounts (and stops reporting) rather than resetting it.
    [MODAL_TABS.USER_EVENTS]: hasLinkedEvents && userEventsCount > 0 ? ` (${userEventsCount})` : '',
    [MODAL_TABS.LOGBOOK]: logbookCount > 0 ? ` (${logbookCount})` : '',
    [MODAL_TABS.COMMENTS]: commentsCount > 0 ? ` (${commentsCount})` : '',
  };

  return (
    <>
      <SimpleModal
        onClose={onClose}
        overlayCanClose={false}
        size="l"
        title={alarm?.algorithmName || 'Alarm Details'}
        className="alarm-modal"
        noBodyPadding
        headerRight={
          isResolved && editMenuOptions.length > 0 ? (
            <DropdownMenu
              trigger="he-moreoptionshorizontal"
              triggerSize="s"
              triggerThemeLightColor={colors.LightApplicationPrimaryText}
              menuOptions={editMenuOptions}
              className="alarm-modal-menu"
              menuXPlacement="left"
              styles={{
                dropdownIndicator: { padding: '0 1rem', filter: 'none' },
                option: { cursor: 'pointer' },
              }}
            />
          ) : undefined
        }
      >
        <div className="modal-column-2">
          <div className="modal-column">
            <div className="alarm-info-section">
              {!isResolved && (
                <>
                  <div className="alarm-info-item">
                    <Label>Latest Severity</Label>
                    <span className="alarm-info-value">
                      {renderSeverity(alarm?.latestSeverity)}
                    </span>
                  </div>
                  <div className="alarm-info-item">
                    <Label>Highest Severity</Label>
                    <span className="alarm-info-value">
                      {renderSeverity(alarm?.highestSeverity)}
                    </span>
                  </div>
                </>
              )}
              <div className="alarm-info-item">
                <Label>Last Occurred At</Label>
                <span className="alarm-info-value">{formatDate(alarm?.lastOccurrence)}</span>
              </div>
              <div className="alarm-info-item">
                <Label>First Occurred At</Label>
                <span className="alarm-info-value">{formatDate(alarm?.firstOccurrence)}</span>
              </div>
              <div className="alarm-info-item">
                <Label>Equipment</Label>
                <span className="alarm-info-value">
                  {alarm?.deviatingReferenceDesignation || '-'}
                </span>
              </div>
            </div>
          </div>
          <div className="modal-column">
            <div className="modal-column--content">
              <EventTimelineProvider
                key={eventTimelineKey}
                site={activeSite}
                dateDomain={relatedEventsDateDomain}
                filterText={relatedEventsValue.text}
                filterParams={relatedEventsValue.params}
                hideInactiveSystemEvents={relatedEventsValue.active}
                enabledEventTypes={['user']}
              >
                {hasLinkedEvents && <UserEventsCount onCountChange={setUserEventsCount} />}
                <HvTabs
                  value={activeModalTab}
                  onChange={(_event, value) => setActiveModalTab(value)}
                >
                  <HvTab label={isResolved ? 'Resolution' : 'Handler'} />
                  <HvTab label={`Source Events${counts[MODAL_TABS.SOURCE_EVENTS]}`} />
                  <HvTab label={`User Events${counts[MODAL_TABS.USER_EVENTS]}`} />
                  <HvTab label={`Logbook${counts[MODAL_TABS.LOGBOOK]}`} />
                  <HvTab label={`Comments${counts[MODAL_TABS.COMMENTS]}`} />
                </HvTabs>
                <div className="alarm-modal-tab-panel">
                  {activeModalTab === MODAL_TABS.HANDLER && (
                    <AlarmHandlerTab
                      alarm={alarm}
                      onAlarmUpdate={handleAlarmUpdate}
                      alarmQueryParameters={alarmQueryParameters}
                    />
                  )}
                  {activeModalTab === MODAL_TABS.SOURCE_EVENTS && <SourceEventsTab alarm={alarm} />}
                  {activeModalTab === MODAL_TABS.USER_EVENTS && (
                    <UserEventsTab
                      alarm={alarm}
                      site={activeSite}
                      relatedEventsValue={relatedEventsValue}
                      hasLinkedEvents={hasLinkedEvents}
                      alarmQueryParameters={alarmQueryParameters}
                    />
                  )}
                  {activeModalTab === MODAL_TABS.LOGBOOK && <LogbookTab alarm={alarm} />}
                  {activeModalTab === MODAL_TABS.COMMENTS && <CommentsTab alarm={alarm} />}
                </div>
              </EventTimelineProvider>
            </div>
          </div>
        </div>
      </SimpleModal>
      {deleteAlarmConfirmation}
    </>
  );
};

export default AlarmModal;
